/**
 * ProductV1ImageEditModal Component
 *
 * Modal for editing Silo & Lifestyle render images on Product Page V1.
 * Wraps EditImageTool with a footer for downloading edited images.
 *
 * Used in:
 * - SilosListShowCase.js (Product Page V1 - Silo images)
 * - ImageryShowCase.js (Product Page V1 - Lifestyle images)
 */

import { useState } from 'react';
import { CloseIcon } from '../assets/icons';
import EditImageTool, { handleDownloadEditedImage } from './EditImageTool';
import BigButton from '../common/BigButton';
import DownloadIcon from './icons/DownloadIcon';
import { download } from '../common/DownloadFileController/downloadFile';
import { warningToast } from '../helpers/toastHelper';
import { getRenderOriginalFile, getRenderOtherFile, getUrlExtension } from '../helpers/jsHelper';

export default function ProductV1ImageEditModal({
  show = false,
  editImageTarget,
  imageToolData,
  setImageToolData,
  onClose,
  onImageUpdate,
}) {
  const [downloading, setDownloading] = useState(false);
  // Set by the editor while an Apply is still being written. Closing then would
  // abandon the upload half way, leaving the stored image out of step with the
  // config that was just saved.
  const [isApplying, setIsApplying] = useState(false);

  if (!show || !editImageTarget) {
    return null;
  }

  const originalFile = getRenderOriginalFile(editImageTarget);
  const otherFile = getRenderOtherFile(editImageTarget);

  /**
   * Renders the edits onto a blob - canvas work, so the button spins for it -
   * then hands the blob to the shared download controller, which reports the
   * save in the Downloads snackbar like every other download in the app.
   */
  const handleDownload = async () => {
    try {
      const fileUrl = await handleDownloadEditedImage({
        // The same base the canvas is showing. Handing it the Post Edit
        // preview instead would apply the effects a second time.
        url: originalFile,
        imageToolData,
        otherImage: otherFile,
        colorPassImage: editImageTarget?.render_image_details?.color_pass_file,
        title: editImageTarget?.name,
        returnFile: true,
        loading: setDownloading,
      });
      if (!fileUrl) return;

      // A blob: URL carries no extension, so it takes the original's - which is
      // also the format the editor encoded it back into.
      const extension = getUrlExtension(originalFile);
      await download({
        fileUrl,
        // Without a name the controller falls back to the one on the URL.
        fileName: [editImageTarget?.name, extension].filter(Boolean).join('.') || undefined,
      });
    } catch (error) {
      console.error('Failed to download the edited image:', error);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#fff',
        '--header-height': '0px',
        '--sidebar-z-index': '10000',
      }}>
      <button
        onClick={() => {
          if (isApplying) {
            warningToast('Please wait - the edited image is still being saved.');
            return;
          }
          onClose();
        }}
        style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          zIndex: 10001,
          background: '#f5f5f5',
          border: '1px solid #ddd',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px',
          borderRadius: '4px',
          pointerEvents: 'auto',
        }}>
        <CloseIcon style={{ color: '#333', width: '14px', height: '14px' }} />
      </button>

      {/**
       * EDIT IMAGE TOOL
       *
       * Purpose: Modal editor for adjusting image effects (brightness, contrast, etc)
       *
       * Key Design Decision:
       * url={getRenderOriginalFile(editImageTarget)}
       *
       * The render as produced - the filmic file for a filmic render, the
       * standard file otherwise. This is the same image the "Original" pass
       * shows and the same base the Post Edit preview is built from, so the
       * three never disagree.
       *
       * Why ALWAYS use the original image (not Post Edit preview):
       * 1. Original is the source of truth for all edits
       * 2. Ensures edits are always relative to the original base
       * 3. Prevents cascading/compounding edits:
       *    - User edits image A: brightness 50
       *    - Saves → POST_EDIT shows edited version
       *    - User clicks Edit again while viewing POST_EDIT
       *    - Without this fix: Editor would use POST_EDIT as base
       *    - With this fix: Editor uses original as base ✓
       * 4. The imageToolData (edit_config) is applied on top of original
       *    so user sees current edits applied to original
       *
       * Flow:
       * Original Image + edit_config effects = What user sees in editor
       */}
      <EditImageTool
        id={editImageTarget?.render_image}
        // The PDP cards key their "Paste & Apply" off the image id, not the
        // render image id, so copies made here have to be stamped with that.
        copyId={editImageTarget?.id}
        url={originalFile}
        data={imageToolData}
        setData={setImageToolData}
        setIsEditImageTool={onClose}
        otherImage={otherFile}
        colorPassImage={editImageTarget?.render_image_details?.color_pass_file}
        setAnyChangeInEditImage={() => {}}
        callCloseFunc={onClose}
        title={editImageTarget?.name}
        onImageUpdate={onImageUpdate}
        editImageTarget={editImageTarget}
        onApplyingChange={setIsApplying}
      />

      <footer
        className="preview-display-footer"
        style={{
          position: 'absolute',
          width: 'calc(100% - 250px)',
          bottom: 0,
          left: '250px',
          right: 0,
        }}>
        <h1 className="title">{editImageTarget?.name || 'Image'}</h1>
        <div className="d-flex align-items-center justify-content-center gap-3 p-1 pe-3">
          <BigButton
            className="p-3 lf-btn-1 d-flex btn-v2"
            isLoading={downloading}
            disabled={downloading}
            onClick={handleDownload}>
            <DownloadIcon /> Download
          </BigButton>
        </div>
      </footer>
    </div>
  );
}
